/** Must match SUPPORTED_UI_LANGUAGES in src/i18n/index.ts and LiveTranslationDropdown. */
const SUPPORTED_UI_LANGUAGES = new Set([
  "en",
  "fr",
  "nl",
  "it",
  "es",
  "ar",
  "zh",
  "pt",
]);

const RTL_LANGUAGE_CODES = new Set(["ar"]);

function normalizeToSupportedCode(lng: string | undefined): string {
  const code = (lng || "en").split("-")[0]?.toLowerCase() ?? "en";
  return SUPPORTED_UI_LANGUAGES.has(code) ? code : "en";
}

/** BCP 47 value for <html lang> — improves IME / spellcheck hints with OS keyboards. */
export function htmlLangAttribute(lng: string | undefined): string {
  const code = normalizeToSupportedCode(lng);
  return code === "zh" ? "zh-Hans" : code;
}

export function documentDirFromLanguage(lng: string | undefined): "ltr" | "rtl" {
  return RTL_LANGUAGE_CODES.has(normalizeToSupportedCode(lng)) ? "rtl" : "ltr";
}

/**
 * Keeps <html lang> and <dir> in sync with i18n so inputs/textareas inherit
 * language + typing direction. Does not switch the physical keyboard — the user
 * still selects Arabic/Chinese/etc. in the OS input settings.
 */
export function syncDocumentHtmlLangAndDir(lng: string | undefined): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = htmlLangAttribute(lng);
  document.documentElement.dir = documentDirFromLanguage(lng);
}

export type LocalizedInputProps = {
  lang: string;
  dir: "ltr" | "rtl";
};

/** Use on Form.Control / input / textarea when you need explicit attributes (e.g. inside a forced-LTR subtree). */
export function localizedInputPropsFromLanguage(lng: string | undefined): LocalizedInputProps {
  return {
    lang: htmlLangAttribute(lng),
    dir: documentDirFromLanguage(lng),
  };
}
